// Web Speech is not yet included in TypeScript's DOM declarations.
export interface SpeechRecognitionResultLike {
  0: { transcript: string }
  isFinal?: boolean
}

export interface SpeechRecognitionLike {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives?: number
  onstart: (() => void) | null
  onresult: ((event: { results: ArrayLike<SpeechRecognitionResultLike>; resultIndex?: number }) => void) | null
  onerror: ((event: { error: string }) => void) | null
  onend: (() => void) | null
  start(): void
  stop(): void
  abort(): void
}

export type SpeechRecognitionConstructor = new () => SpeechRecognitionLike
export type DictationStatus = 'idle' | 'starting' | 'listening' | 'stopping'

export function getSpeechRecognition(): SpeechRecognitionConstructor | undefined {
  if (typeof window === 'undefined' || !window.isSecureContext) return undefined
  const browser = window as Window & {
    SpeechRecognition?: SpeechRecognitionConstructor
    webkitSpeechRecognition?: SpeechRecognitionConstructor
  }
  return browser.SpeechRecognition ?? browser.webkitSpeechRecognition
}

const ERRORS: Record<string, string> = {
  'not-allowed': 'Microphone access was blocked. Allow the microphone in your browser’s site settings, then try again.',
  'service-not-allowed': 'Your browser has blocked speech recognition. Check its permissions or try another browser.',
  'audio-capture': 'No microphone is available. Connect a microphone and try again.',
  'no-speech': 'No speech was detected. Try again and speak after “Listening” appears.',
  network: 'Speech recognition could not connect. Check your connection and try again. Your text is kept.',
  'language-not-supported': 'This speech language is unavailable. Choose another language or dialect in Settings → Voice, then try again.',
  aborted: 'Dictation was interrupted. Your text is kept; you can start again.',
}

/**
 * How long one dictation may run before it is stopped for the person, and how
 * long a recording may stay silent from the first “Listening” before it gives
 * up and says so. Both are generous on purpose: a description is often a
 * minute of speech with pauses, and the pauses are exactly where browsers
 * used to cut the recording off.
 */
export const DICTATION_MAX_MS = 180_000
export const DICTATION_SILENCE_GRACE_MS = 20_000

/**
 * One recording, independent of React so late browser events and cleanup can be tested.
 *
 * Browsers do not keep a recognition open for as long as the person keeps
 * talking. Chrome ends it after a few seconds of silence — a pause between two
 * sentences — and reports `no-speech` if nothing was said in the first
 * seconds; Safari ends it on its own schedule, and after a restart reports a
 * results list that starts again from zero. Every one of those used to look
 * like “the microphone stopped working mid-sentence”. This session hides them:
 * when a recognition ends while the person is still dictating it is started
 * again, the words it had heard are kept, and the next recognition's results
 * are appended after them.
 *
 * Text is built from the whole results snapshot of the CURRENT recognition —
 * which handles revised, extended and withdrawn interim words without
 * duplicating finals — and prefixed with what earlier recognitions committed.
 */
export function createDictationSession(
  Recognition: SpeechRecognitionConstructor,
  options: {
    language: string
    initialText: string
    onText: (text: string) => void
    onStatus: (status: DictationStatus) => void
    onError: (message: string) => void
  },
) {
  const prefix = options.initialText.trimEnd()
  let recognition: SpeechRecognitionLike | null = null
  let active = false
  let stopping = false
  let listening = false
  /** Words from recognitions that already ended, in order. */
  let committed = ''
  /** Words from the recognition that is running now. */
  let current = ''
  let heardSpeech = false
  let startedAt: number | null = null
  let timer: ReturnType<typeof setTimeout> | undefined
  let limit: ReturnType<typeof setTimeout> | undefined

  function joined(...parts: string[]): string {
    return parts.filter(Boolean).join(' ')
  }

  function publish() {
    const spoken = joined(committed, current)
    options.onText(spoken ? joined(prefix, spoken) : options.initialText)
  }

  function detach(rec: SpeechRecognitionLike | null) {
    if (!rec) return
    rec.onstart = null
    rec.onresult = null
    rec.onerror = null
    rec.onend = null
    try { rec.abort() } catch { /* Already ended or never started. */ }
  }

  function dispose() {
    active = false
    listening = false
    clearTimeout(timer)
    clearTimeout(limit)
    detach(recognition)
    recognition = null
  }

  function cancel() {
    dispose()
    options.onStatus('idle')
  }

  function finish() {
    if (!active) return
    cancel()
    if (!heardSpeech) options.onError(ERRORS['no-speech']!)
  }

  function fail(code: string) {
    if (!active) return
    cancel()
    options.onError(ERRORS[code] ?? 'Dictation stopped unexpectedly. Your text is kept; please try again.')
  }

  function stop() {
    if (!active || stopping) return
    stopping = true
    options.onStatus('stopping')
    clearTimeout(timer)
    clearTimeout(limit)
    // Some implementations never emit end after stop; retain the last text
    // and release the microphone instead of leaving the form disabled.
    timer = setTimeout(finish, 4000)
    try { recognition?.stop() } catch { finish() }
  }

  function silentFor(): number {
    return startedAt === null ? 0 : Date.now() - startedAt
  }

  /**
   * The recognition ended on its own while the person is still dictating.
   * Keep its words and start another one, unless nothing has been heard for
   * the whole grace period — then this really is silence.
   */
  function rollOver() {
    if (!active || stopping) return
    committed = joined(committed, current)
    current = ''
    if (!heardSpeech && silentFor() >= DICTATION_SILENCE_GRACE_MS) {
      finish()
      return
    }
    detach(recognition)
    recognition = null
    listening = false
    begin(false)
  }

  function begin(first: boolean) {
    const rec = new Recognition()
    recognition = rec
    rec.lang = options.language
    rec.continuous = true
    rec.interimResults = true
    if ('maxAlternatives' in rec) rec.maxAlternatives = 1
    let sawStart = false

    rec.onstart = () => {
      if (!active || stopping || recognition !== rec) return
      sawStart = true
      listening = true
      clearTimeout(timer)
      if (first) {
        startedAt = Date.now()
        options.onStatus('listening')
        limit = setTimeout(stop, DICTATION_MAX_MS)
      }
    }
    rec.onresult = (event) => {
      if (!active || recognition !== rec) return
      // results is the complete snapshot for THIS recognition. Rebuilding it
      // handles revised/removed interim words and avoids duplicating finals.
      current = Array.from(event.results, (result) => result[0].transcript.trim())
        .filter(Boolean).join(' ')
      if (current) heardSpeech = true
      publish()
    }
    rec.onerror = (event) => {
      if (!active || recognition !== rec) return
      if (event.error === 'no-speech' && !stopping) {
        // A pause, not a failure. Chrome raises this after a few silent
        // seconds; onend follows and rollOver decides whether to continue.
        return
      }
      fail(event.error)
    }
    rec.onend = () => {
      if (!active || recognition !== rec) return
      if (stopping) {
        finish()
        return
      }
      // A recognition that ended without ever starting is a failure, not a
      // pause: restarting it would loop.
      if (!sawStart) {
        finish()
        return
      }
      rollOver()
    }

    clearTimeout(timer)
    // Permissions can take time, but a browser that never responds must not
    // leave the form stuck. The microphone control can also cancel immediately.
    // A restart that stalls simply ends the dictation with its text kept.
    timer = setTimeout(() => {
      if (!active) return
      if (first) {
        cancel()
        options.onError('Dictation did not start. Check microphone permissions and try again.')
      } else {
        finish()
      }
    }, first ? 60_000 : 10_000)
    try { rec.start() } catch {
      if (first) {
        cancel()
        options.onError('Dictation could not start. Check microphone access and try again.')
      } else {
        finish()
      }
    }
  }

  return {
    start() {
      if (active) return
      active = true
      options.onStatus('starting')
      begin(true)
    },
    stop,
    cancel,
    dispose,
    /** Whether a recognition is currently open. Exposed for tests. */
    get listening() { return listening },
  }
}
